import { ExpressionSpecification, FilterSpecification, SymbolLayerSpecification } from 'mapbox-gl';
import { Layer } from 'react-map-gl/mapbox';
import { COLOR_TEXT_PRIMARY_INVERSE, FONT_SIZE_100 } from '@alltrails/denali/tokens';
import { useExperiments } from '@alltrails/experiments';
import useMap from '../../hooks/useMap';
import {
  buildCollapsedPinFilters,
  buildCollapsedTrailIconImage,
  buildDynamicTrailPinProps,
  buildNewCollapsedTrailIconImage
} from '../../utils/buildPinIconExpressions';
import { collapsedTrailPinsActiveId, collapsedTrailPinsId, getLayerBeforeId } from '../../utils/layers';
import {
  newPinMarkerVerticalOffset,
  pinMarkerCollapsedVerticalOffset,
  pinMarkerVerticalOffset,
  trailLengthPinVerticalOffset
} from '../../utils/svgDefinitions/svgMarkerComponents';
import {
  isTrailClosedExpression,
  isTrailCollapsedExpression,
  isTrailCompletedExpression,
  isTrailSavedExpression
} from '../../utils/trailPinConstants';

// For text-offset, we need to divide by the font size (FONT_SIZE_100) to get the correct value
const halfIconWidth = 8;
const horizontalTextOffsetWithIcon = (-1 * halfIconWidth) / FONT_SIZE_100;
const verticalTextOffset = (-1 * newPinMarkerVerticalOffset) / FONT_SIZE_100;

type CollapsedTrailPinLayerProps = {
  sourceId: string;
  isHoveredExpression: ExpressionSpecification;
  isSelectedExpression: ExpressionSpecification;
  isViewedExpression: ExpressionSpecification;
  isActiveTrailExpression: ExpressionSpecification;
  commonLayoutProps: SymbolLayerSpecification['layout'];
};

const CollapsedTrailPinLayer = ({
  sourceId,
  isHoveredExpression,
  isSelectedExpression,
  isViewedExpression,
  isActiveTrailExpression,
  commonLayoutProps
}: CollapsedTrailPinLayerProps) => {
  const map = useMap();
  const experiments = useExperiments();
  const hasNewPinsExperiment = experiments?.['web-discover-new-pin-explore-rm']?.value === 'on';
  const { isCollapsedInactiveFilter, isCollapsedActiveFilter } = buildCollapsedPinFilters(
    isHoveredExpression,
    isSelectedExpression,
    isActiveTrailExpression
  );
  const isActiveOrHovered = ['any', isHoveredExpression, isSelectedExpression, isActiveTrailExpression];
  const newCollapsedTrailIconImage = buildNewCollapsedTrailIconImage(
    isHoveredExpression,
    isSelectedExpression,
    isViewedExpression,
    isActiveTrailExpression
  );
  const collapsedTrailIconImage = buildCollapsedTrailIconImage(isHoveredExpression, isSelectedExpression, isActiveTrailExpression);
  const textFont = ['AllTrails Aeonik Medium', 'Manrope Bold'];
  const newPinLayoutProps = buildDynamicTrailPinProps(isHoveredExpression, isSelectedExpression);

  return (
    <>
      {hasNewPinsExperiment ? (
        <>
          {/* Inactive collapsed pins (no text) - use icon-offset so small pill sits correctly */}
          <Layer
            source={sourceId}
            type="symbol"
            id={collapsedTrailPinsId}
            beforeId={getLayerBeforeId(map, collapsedTrailPinsId)}
            filter={isCollapsedInactiveFilter}
            layout={{
              ...commonLayoutProps,
              'icon-text-fit': 'none',
              'text-field': '',
              'text-font': textFont,
              'icon-offset': [0, trailLengthPinVerticalOffset],
              'icon-image': newCollapsedTrailIconImage
            }}
          />
          {/* Active/hovered collapsed pins - no icon-offset to avoid jump when expanding, text-offset is calculated to position the text correctly */}
          {/* text-offset is calculated to position the text correctly */}
          {/* rendered on top of other pins as per layers.ts */}
          <Layer
            source={sourceId}
            type="symbol"
            id={collapsedTrailPinsActiveId}
            beforeId={getLayerBeforeId(map, collapsedTrailPinsActiveId)}
            filter={isCollapsedActiveFilter}
            layout={{
              ...newPinLayoutProps,
              'text-offset': [
                'case',
                ['any', isTrailCompletedExpression, isTrailSavedExpression, isTrailClosedExpression],
                [horizontalTextOffsetWithIcon, verticalTextOffset],
                [0, verticalTextOffset]
              ],
              'icon-image': newCollapsedTrailIconImage
            }}
            paint={{
              'text-color': COLOR_TEXT_PRIMARY_INVERSE
            }}
          />
        </>
      ) : (
        <Layer
          source={sourceId}
          type="symbol"
          id={collapsedTrailPinsId}
          beforeId={getLayerBeforeId(map, collapsedTrailPinsId)}
          filter={['all', ['!', ['has', 'point_count']], isTrailCollapsedExpression] as FilterSpecification}
          layout={{
            ...commonLayoutProps,
            'icon-offset': ['case', isActiveOrHovered, [0, pinMarkerVerticalOffset], [0, pinMarkerCollapsedVerticalOffset]],
            'icon-image': collapsedTrailIconImage
          }}
        />
      )}
    </>
  );
};

export default CollapsedTrailPinLayer;
